import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateUserProfileName, updateUserEmailNotificationSettings, getUserEmailNotificationSettings } from '../services/userService';
import { Container, TextField, Button, Typography, Paper, Box, Alert, CircularProgress, Switch, FormControlLabel, Divider } from '@mui/material';

const AccountPage = () => {
  const { firestoreUser, setFirestoreUser } = useAuth(); // Assuming setFirestoreUser updates the context
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
    // Email notification settings
  const [emailNotifications, setEmailNotifications] = useState({
    campaignEpisodeCreated: false
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // Admin email notification settings (separate from user preferences)
  const [adminEmailSettings, setAdminEmailSettings] = useState({
    campaignEpisodeCreated: false
  });
  const [loadingAdminSettings, setLoadingAdminSettings] = useState(false);  useEffect(() => {
    const loadEmailNotificationSettings = async () => {
      try {
        setLoadingNotifications(true);
        const settings = await getUserEmailNotificationSettings(firestoreUser.uid);
        setEmailNotifications(settings || { campaignEpisodeCreated: false });
      } catch (err) {
        console.error("Error loading email notification settings:", err);
      } finally {
        setLoadingNotifications(false);
      }
    };

    const loadAdminEmailSettings = async () => {
      try {
        setLoadingAdminSettings(true);
        const settings = await getUserEmailNotificationSettings(firestoreUser.uid);
        setAdminEmailSettings(settings?.adminSettings || { campaignEpisodeCreated: false });
      } catch (err) {
        console.error("Error loading admin email settings:", err);
      } finally {
        setLoadingAdminSettings(false);
      }
    };

    if (firestoreUser) {
      setDisplayName(firestoreUser.name || '');
      
      // Load user email notification settings for all users
      loadEmailNotificationSettings();
      
      // Load admin email notification settings if user is admin
      if (firestoreUser.roles && firestoreUser.roles.admin) {
        loadAdminEmailSettings();
      }
    }
  }, [firestoreUser]);
  const handleEmailNotificationChange = async (settingKey, value) => {
    try {
      setLoadingNotifications(true);
      const newSettings = { ...emailNotifications, [settingKey]: value };
      await updateUserEmailNotificationSettings(firestoreUser.uid, newSettings);
      setEmailNotifications(newSettings);
      setSuccess("Email notification settings updated!");
    } catch (err) {
      console.error("Error updating email notification settings:", err);
      setError("Failed to update email notification settings.");
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleAdminEmailNotificationChange = async (settingKey, value) => {
    try {
      setLoadingAdminSettings(true);
      const newAdminSettings = { ...adminEmailSettings, [settingKey]: value };
      const currentSettings = await getUserEmailNotificationSettings(firestoreUser.uid);
      const updatedSettings = { 
        ...currentSettings, 
        adminSettings: newAdminSettings 
      };
      await updateUserEmailNotificationSettings(firestoreUser.uid, updatedSettings);
      setAdminEmailSettings(newAdminSettings);
      setSuccess("Admin email notification settings updated!");
    } catch (err) {
      console.error("Error updating admin email notification settings:", err);
      setError("Failed to update admin email notification settings.");
    } finally {
      setLoadingAdminSettings(false);
    }
  };

  const handleNameChange = (e) => {
    setDisplayName(e.target.value);
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!firestoreUser || !firestoreUser.uid) {
      setError("User not found. Please log in again.");
      return;
    }
    if (!displayName.trim()) {
      setError("Display name cannot be empty.");
      return;
    }
    if (displayName.trim() === firestoreUser.name) {
      setIsEditingName(false); // No change, just close edit mode
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await updateUserProfileName(firestoreUser.uid, displayName.trim());
      // Update context/local state if necessary
      // This assumes your AuthContext has a way to update the firestoreUser object
      if (setFirestoreUser) {
        setFirestoreUser(prevUser => ({ ...prevUser, name: displayName.trim() }));
      }
      setSuccess("Display name updated successfully!");
      setIsEditingName(false);
    } catch (err) {
      console.error("Error updating name:", err);
      setError(err.message || "Failed to update display name. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!firestoreUser) {
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ padding: 3, textAlign: 'center', backgroundColor: '#333', color: '#fff' }}>
          <Typography variant="h6">Loading user data...</Typography>
          <CircularProgress sx={{ mt: 2, color: '#d98d2a' }} />
        </Paper>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={6} sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: '#2c2c2c', color: '#f5f5f5', border: '1px solid #d98d2a' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#d98d2a', fontFamily: 'Georgia, serif', textAlign: 'center' }}>
          Account Details
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, bgcolor: '#ff6b6b', color: 'white' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, bgcolor: '#4caf50', color: 'white' }}>{success}</Alert>}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#d98d2a' }}>Email:</Typography>
          <Typography sx={{ color: '#f5f5f5', overflowWrap: 'break-word' }}>{firestoreUser.email}</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#d98d2a' }}>Display Name:</Typography>
          {!isEditingName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ color: '#f5f5f5' }}>{firestoreUser.name || 'Not set'}</Typography>
              <Button 
                variant="outlined" 
                onClick={() => setIsEditingName(true)}
                sx={{ 
                  color: '#d98d2a', 
                  borderColor: '#d98d2a',
                  '&:hover': { borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)' }
                }}
              >
                Edit Name
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleUpdateName}>
              <TextField
                label="Display Name"
                value={displayName}
                onChange={handleNameChange}
                fullWidth
                required
                margin="normal"
                InputLabelProps={{ style: { color: '#d98d2a' } }}
                inputProps={{ style: { color: '#f5f5f5' } }}
                sx={{ 
                  mb: 1, 
                  '& .MuiOutlinedInput-root': { 
                    '& fieldset': { borderColor: '#d98d2a' },
                    '&:hover fieldset': { borderColor: '#c87d26' },
                  } 
                }}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  onClick={() => {
                    setIsEditingName(false);
                    setDisplayName(firestoreUser.name || ''); // Reset to original name
                    setError(''); 
                  }} 
                  sx={{ mr: 1, color: '#f5f5f5' }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading || displayName.trim() === firestoreUser.name}
                  sx={{ 
                    backgroundColor: '#d98d2a', 
                    color: '#1c1c1c',
                    '&:hover': { backgroundColor: '#c87d26' },
                    '&.Mui-disabled': { backgroundColor: '#555', color: '#888' }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{color: '#1c1c1c'}} /> : 'Save Name'}
                </Button>
              </Box>
            </form>
          )}
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#d98d2a' }}>User ID:</Typography>
          <Typography sx={{ color: '#aaa', fontSize: '0.9rem', overflowWrap: 'break-word' }}>{firestoreUser.uid}</Typography>
        </Box>        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#d98d2a' }}>Roles:</Typography>
          {firestoreUser.roles && Object.entries(firestoreUser.roles).map(([role, hasRole]) => (
            hasRole && <Typography key={role} sx={{ color: '#f5f5f5', textTransform: 'capitalize' }}>- {role.replace(/([A-Z])/g, ' $1')}</Typography>
          ))}
          {(!firestoreUser.roles || Object.values(firestoreUser.roles).every(v => !v)) && (
            <Typography sx={{ color: '#f5f5f5' }}>No special roles assigned.</Typography>
          )}
        </Box>

        {/* User Email Notification Preferences - Available to All Users */}
        <Divider sx={{ mb: 3, backgroundColor: '#555' }} />
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#d98d2a', mb: 2 }}>
            Email Notifications
          </Typography>
          <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
            Choose which email notifications you'd like to receive
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={emailNotifications.campaignEpisodeCreated}
                onChange={(e) => handleEmailNotificationChange('campaignEpisodeCreated', e.target.checked)}
                disabled={loadingNotifications}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#d98d2a',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#d98d2a',
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ color: '#f5f5f5' }}>
                  Campaign Episode Notifications
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  Receive emails when new campaign episodes are posted
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />
          
          {loadingNotifications && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={16} sx={{ color: '#d98d2a', mr: 1 }} />
              <Typography variant="caption" sx={{ color: '#aaa' }}>
                Updating settings...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Admin Email Notification Settings - Only for Admins */}
        {firestoreUser.roles && firestoreUser.roles.admin && (
          <>
            <Divider sx={{ mb: 3, backgroundColor: '#555' }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#d98d2a', mb: 2 }}>
                Admin Email Notification Controls
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
                Configure when the system should send campaign episode notifications to users
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={adminEmailSettings.campaignEpisodeCreated}
                    onChange={(e) => handleAdminEmailNotificationChange('campaignEpisodeCreated', e.target.checked)}
                    disabled={loadingAdminSettings}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#d98d2a',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#d98d2a',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ color: '#f5f5f5' }}>
                      Enable Campaign Episode Email System
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      When enabled, email notification buttons will appear for admins to send notifications
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              
              {loadingAdminSettings && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#d98d2a', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#aaa' }}>
                    Updating admin settings...
                  </Typography>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2, backgroundColor: '#1a237e', color: '#e3f2fd' }}>
                <Typography variant="caption">
                  <strong>Note:</strong> This setting controls whether the email notification system is active. 
                  Users will only receive emails if both this admin setting is ON and their personal email preference is ON.
                </Typography>
              </Alert>
            </Box>
          </>
        )}

        {firestoreUser.createdAt && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#d98d2a' }}>Joined:</Typography>
            <Typography sx={{ color: '#f5f5f5' }}>
              {new Date(firestoreUser.createdAt.seconds * 1000).toLocaleDateString()}
            </Typography>
          </Box>
        )}

        {firestoreUser.lastLoginAt && (
          <Box>
            <Typography variant="h6" sx={{ color: '#d98d2a' }}>Last Login:</Typography>
            <Typography sx={{ color: '#f5f5f5' }}>
              {new Date(firestoreUser.lastLoginAt.seconds ? firestoreUser.lastLoginAt.seconds * 1000 : firestoreUser.lastLoginAt).toLocaleString()}
            </Typography>
          </Box>
        )}

      </Paper>
    </Container>
  );
};

export default AccountPage;
